import type { TrackingEventByTopic } from '@/lib/eventBus/trackingEvents'

export type EventHandler<T> = (payload: T) => void | Promise<void>

export interface EventSubscription {
  unsubscribe: () => void
}

/** TEventByTopic maps each topic to its payload, so emit and subscribe reject a payload of the wrong shape. */
export interface EventBus<TEventByTopic> {
  emit: <Topic extends keyof TEventByTopic>(topic: Topic, payload: TEventByTopic[Topic]) => void
  subscribe: <Topic extends keyof TEventByTopic>(
    topic: Topic,
    handler: EventHandler<TEventByTopic[Topic]>
  ) => EventSubscription
}

const deliver = async <T>(handler: EventHandler<T>, payload: T) => handler(payload)

export const createEventBus = <TEventByTopic extends object = Record<string, unknown>>(): EventBus<TEventByTopic> => {
  // biome-ignore lint/suspicious/noExplicitAny: handlers are keyed by topic, each topic owns its own payload type
  const handlersByTopic = new Map<keyof TEventByTopic, Set<EventHandler<any>>>()

  const emit = <Topic extends keyof TEventByTopic>(topic: Topic, payload: TEventByTopic[Topic]) => {
    handlersByTopic.get(topic)?.forEach((handler) => {
      deliver(handler, payload).catch((error: unknown) => {
        // A subscriber's failure must never stop delivery to sibling subscribers, and must never
        // propagate into the caller's flow (e.g. checkout) — analytics is best-effort by design.
        console.error(`[eventBus] subscriber for "${String(topic)}" threw:`, error)
      })
    })
  }

  const subscribe = <Topic extends keyof TEventByTopic>(
    topic: Topic,
    handler: EventHandler<TEventByTopic[Topic]>
  ): EventSubscription => {
    const handlers = handlersByTopic.get(topic) ?? new Set()
    handlers.add(handler)
    handlersByTopic.set(topic, handlers)

    return { unsubscribe: () => handlers.delete(handler) }
  }

  return { emit, subscribe }
}

/** App-wide singleton: GA4/Relewise subscribers register once (registerAnalyticsSubscribers), trigger
 * hooks (useTrackEvent) emit onto it from anywhere in the tree without prop-drilling a bus instance. */
export const eventBus = createEventBus<TrackingEventByTopic>()
